import React, { useState, useEffect } from "react";
import { useUseCaseConfig } from "./useUseCaseConfig";
import * as S from "../shared/styles";
import { ROLE, ROLE_LABELS, RoleValue, PermissionsMember } from "../shared/types";
import auditModuleService from "src/services/auditModuleService";

// ----------------------------  PermissionsPage  ----------------------------

const PermissionsPage: React.FC = () => {
  const { configs } = useUseCaseConfig();
  const [members, setMembers] = useState<PermissionsMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    auditModuleService
      .getPermissionMembers()
      .then((data) => {
        if (!cancelled) setMembers(data);
      })
      .catch((err) => {
        if (cancelled) return;
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail;
        if (status === 403) {
          setError(
            "You do not have permission to manage the permissions group. " +
              "An audit Admin or Program Owner role is required (HTTP 403).",
          );
        } else if (status) {
          setError("Failed to load members (HTTP " + status + (detail ? ": " + detail : "") + ").");
        } else {
          setError("Failed to load members. Please refresh to try again.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const toggleAssignment = async (login: string, ucId: string) => {
    const member = members.find((m) => m.login === login);
    if (!member) return;
    const cur = member.assigned_use_case_ids;
    const next = cur.includes(ucId) ? cur.filter((x) => x !== ucId) : [...cur, ucId];
    const prev = members;
    setMembers((p) =>
      p.map((m) => (m.login === login ? { ...m, assigned_use_case_ids: next } : m)),
    );
    try {
      const updated = await auditModuleService.setMemberUseCases(login, next);
      setMembers((p) => p.map((m) => (m.login === login ? updated : m)));
    } catch {
      setMembers(prev);
    }
  };

  const changeRole = async (login: string, role: RoleValue) => {
    const prev = members;
    setMembers((p) => p.map((m) => (m.login === login ? { ...m, role } : m)));
    try {
      const updated = await auditModuleService.updateMemberRole(login, role);
      setMembers((p) => p.map((m) => (m.login === login ? updated : m)));
    } catch {
      setMembers(prev);
    }
  };

  const removeMember = async (login: string) => {
    const prev = members;
    setMembers((p) => p.filter((m) => m.login !== login));
    try {
      await auditModuleService.deletePermissionMember(login);
    } catch {
      setMembers(prev);
    }
  };

  return (
    <div style={S.pageWrapper}>
      {/* Header */}
      <div style={{ marginBottom: "20px" }}>
        <h1 style={S.pageTitle}>Permissions &amp; Access</h1>
        <p style={S.pageSubtitle}>
          Manage the Excavator Permissions Group, roles, and per-use-case auditor assignments.
        </p>
      </div>

      {/* Members table */}
      <div style={S.configCard}>
        <div style={cardHeader}>
          <span style={cardTitle}>Permissions Group ({members.length})</span>
        </div>

        {loading ? (
          <p style={statusText}>Loading members…</p>
        ) : error ? (
          <p style={{ ...statusText, color: "#ef4444" }}>{error}</p>
        ) : (
          <table style={{ width: "100%", fontSize: "0.875rem", borderCollapse: "collapse" }}>
            <thead>
              <tr style={theadRow}>
                <th style={th}>Member</th>
                <th style={th}>Role</th>
                <th style={th}>Authorized Use Cases</th>
                <th style={{ ...th, width: "80px" }}></th>
              </tr>
            </thead>
            <tbody>
              {members.map((m) => {
                const elevated = m.elevated || m.role === ROLE.ADMIN;
                return (
                  <tr key={m.login} style={tbodyRow}>
                    <td style={td}>
                      <div style={{ fontWeight: 500, color: "#1f2937" }}>{m.name}</div>
                      <div style={{ fontSize: "0.75rem", color: "#9ca3af" }}>{m.login}</div>
                    </td>
                    <td style={td}>
                      <select
                        value={m.role}
                        onChange={(e) => changeRole(m.login, e.target.value as RoleValue)}
                        style={roleSelect}
                      >
                        {Object.values(ROLE).map((r) => (
                          <option key={r} value={r}>
                            {ROLE_LABELS[r]}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td style={td}>
                      {elevated ? (
                        <span style={{ fontSize: "0.75rem", color: "#9ca3af" }}>
                          All use cases (elevated role)
                        </span>
                      ) : (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                          {configs.map((u) => {
                            const on = m.assigned_use_case_ids.includes(u.id);
                            return (
                              <button
                                key={u.id}
                                onClick={() => toggleAssignment(m.login, u.id)}
                                style={on ? ucPillOn : ucPillOff}
                              >
                                {u.name}
                              </button>
                            );
                          })}
                          {m.assigned_use_case_ids.length === 0 && (
                            <span style={{ fontSize: "0.75rem", color: "#ef4444" }}>
                              No access -- assign at least one
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                    <td style={{ ...td, textAlign: "right" }}>
                      <button style={removeBtn} onClick={() => removeMember(m.login)}>
                        Remove
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        <p style={{ fontSize: "0.75rem", color: "#9ca3af", marginTop: "12px" }}>
          Auditors only see tasks for their assigned use cases. Changes take effect immediately and
          are logged to the audit trail.
        </p>
      </div>
    </div>
  );
};

// ----------------------------  Styles  ----------------------------

const cardHeader: React.CSSProperties = {
  marginBottom: "16px",
};

const cardTitle: React.CSSProperties = {
  fontSize: "1rem",
  fontWeight: 600,
  color: "#111827",
};

const statusText: React.CSSProperties = {
  fontSize: "0.875rem",
  color: "#6b7280",
  padding: "16px 0",
};

const theadRow: React.CSSProperties = {
  textAlign: "left",
  fontSize: "0.75rem",
  color: "#9ca3af",
  borderBottom: "1px solid #e5e7eb",
};

const th: React.CSSProperties = {
  padding: "8px 12px 8px 0",
  fontWeight: 500,
};

const tbodyRow: React.CSSProperties = {
  borderBottom: "1px solid #f3f4f6",
  verticalAlign: "top",
};

const td: React.CSSProperties = {
  padding: "12px 12px 12px 0",
};

const roleSelect: React.CSSProperties = {
  border: "1px solid #d1d5db",
  borderRadius: "4px",
  padding: "4px 8px",
  fontSize: "0.875rem",
  background: "white",
};

const ucPillBase: React.CSSProperties = {
  fontSize: "0.75rem",
  padding: "2px 8px",
  borderRadius: "9999px",
  border: "1px solid",
  fontWeight: 500,
  cursor: "pointer",
  background: "none",
};

const ucPillOn: React.CSSProperties = {
  ...ucPillBase,
  background: "#0f7b8a",
  color: "white",
  borderColor: "#0f7b8a",
};

const ucPillOff: React.CSSProperties = {
  ...ucPillBase,
  background: "none",
  color: "#6b7280",
  borderColor: "#d1d5db",
};

const removeBtn: React.CSSProperties = {
  background: "none",
  border: "none",
  cursor: "pointer",
  fontSize: "0.75rem",
  color: "#ef4444",
  padding: 0,
};

export default PermissionsPage;
